import React from 'react';
import {env} from '@/matic/config/env';
import {PonyExpress} from './PonyExpress';

type LoadingSpinnerProps = {
  duration?: number; // Optional prop to control animation duration
  size?: number; // Optional prop to control width/height, default 120
  animated?: boolean; // Optional prop to control if the SVG should be animated or static, default true
  showProgressBar?: boolean; // Render a progress bar shell (updated via DOM by the download hook)
};

export default function LoadingSpinner({
  duration = 0.8,
  size = 120,
  animated = true,
  showProgressBar = false,
}: LoadingSpinnerProps) {
  // The AES deployment's loading mark is the Pony Express rider, wherever the M would be.
  // It's the deployment's brand, not a tenant's: it shows before anyone signs in.
  // Drawn at the M's height scaled so the rider's width is about the M's.
  const mark =
    env.WHITE_LABEL === 'aes' ? (
      <PonyExpress
        size={Math.round(size * 0.78)}
        className={animated ? undefined : 'pony-still'}
      />
    ) : null;

  return (
    <div
      role="status"
      aria-label="Loading"
      className="flex flex-col items-center justify-center gap-3 bg-transparent"
    >
      {mark}
      {!mark && animated && (
        <style
          dangerouslySetInnerHTML={{
            // eslint-disable-next-line @typescript-eslint/naming-convention
            __html: `
        @keyframes trace {
          0% { stroke-dashoffset: 650; }
          100% { stroke-dashoffset: 0; }
        }
      `,
          }}
        />
      )}
      {!mark && (
        <svg
          width={size}
          height={size}
          viewBox="0 0 100 100"
          xmlns="http://www.w3.org/2000/svg"
          className="overflow-visible"
        >
          {/* Animated tracer - mathematically perfect Space Grotesk M outline */}
          <path
            d="M 10 90 L 10 16 L 38 16 L 50 80 L 62 16 L 90 16 L 90 90 L 74 90 L 74 36 L 64 90 L 36 90 L 26 36 L 26 90 Z"
            fill={animated ? 'none' : '#FF6B35'}
            stroke="#FF6B35"
            strokeWidth={animated ? '2.5' : '3'}
            strokeLinecap="square"
            strokeLinejoin="miter"
            strokeDasharray={animated ? '650' : 'none'}
            strokeDashoffset={animated ? '650' : '0'}
            style={
              animated
                ? {
                    animation: `trace ${duration}s ease-in-out infinite alternate`,
                  }
                : undefined
            }
          />
        </svg>
      )}
      {showProgressBar && (
        <div className="flex w-32 flex-col items-center gap-1.5">
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-neutral-200">
            <div
              data-download-progress-bar=""
              className="h-full rounded-full bg-orange-500 transition-all duration-200 ease-out"
              style={{width: '0%'}}
            />
          </div>
          <span
            data-download-progress-text=""
            className="text-xs tabular-nums text-neutral-500"
          >
            0%
          </span>
        </div>
      )}
    </div>
  );
}
